import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { LangProvider } from '@/contexts/LangContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { NavBadgesProvider } from '@/contexts/NavBadgesContext';
import AppLayout from '@/components/AppLayout';
import LoadingScreen from '@/components/LoadingScreen';
import useAppVersionWatcher from '@/hooks/useAppVersionWatcher';

// Eagerly imported — these are on the critical path (initial paint after auth resolve).
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Landing from '@/pages/Landing';

// Everything else is code-split. React.lazy returns a Suspended component
// → only fetched the first time the route is visited. Massive boot-time win.
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const Feed = lazy(() => import('@/pages/Feed'));
const FeedByUser = lazy(() => import('@/pages/FeedByUser'));
const Coach = lazy(() => import('@/pages/Coach'));
const Profile = lazy(() => import('@/pages/Profile'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const Admin = lazy(() => import('@/pages/Admin'));
const Goals = lazy(() => import('@/pages/Goals'));
const GoalDetail = lazy(() => import('@/pages/GoalDetail'));
const Friends = lazy(() => import('@/pages/Friends'));
const UserProfile = lazy(() => import('@/pages/UserProfile'));
const Achievements = lazy(() => import('@/pages/Achievements'));
const SetupUsername = lazy(() => import('@/pages/SetupUsername'));
const Invite = lazy(() => import('@/pages/Invite'));
const ChallengeBoard = lazy(() => import('@/pages/ChallengeBoard'));
const JoinHandle = lazy(() => import('@/pages/JoinHandle'));
const DMList = lazy(() => import('@/pages/DMList'));
const DMChat = lazy(() => import('@/pages/DMChat'));
const Groups = lazy(() => import('@/pages/Groups'));
const GroupCreate = lazy(() => import('@/pages/GroupCreate'));
const GroupDetail = lazy(() => import('@/pages/GroupDetail'));
const GroupJoin = lazy(() => import('@/pages/GroupJoin'));
const PostDetail = lazy(() => import('@/pages/PostDetail'));
const Terms = lazy(() => import('@/pages/Terms'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Redeem = lazy(() => import('@/pages/Redeem'));
const FutureMe = lazy(() => import('@/pages/FutureMe'));
const Marketplace = lazy(() => import('@/pages/Marketplace'));
const MarketplaceCategory = lazy(() => import('@/pages/MarketplaceCategory'));
const MarketplaceSaved = lazy(() => import('@/pages/MarketplaceSaved'));
const BlockedUsers = lazy(() => import('@/pages/BlockedUsers'));

function Private({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RoleGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'super_admin' && (!user.username || !user.display_name)) return <Navigate to="/setup-username" replace />;
  if (!user.onboarded && user.role !== 'super_admin') return <Navigate to="/onboarding" replace />;
  return children;
}

function Root() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  // Unauthenticated visitors land on the marketing landing page (with login
  // CTAs). Once they sign in, normal role-based routing takes over.
  if (!user) return <Landing />;
  if (user.role === 'super_admin') return <Navigate to="/admin" replace />;
  if (!user.username || !user.display_name) return <Navigate to="/setup-username" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return <Navigate to="/app" replace />;
}

function App() {
  useAppVersionWatcher();
  return (
    <ThemeProvider>
      <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <NavBadgesProvider>
          <Toaster richColors position="top-center" />
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route path="/" element={<Root />} />
              <Route path="/login" element={<Login />} />
              <Route path="/join" element={<Login />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/setup-username" element={<Private><SetupUsername /></Private>} />
              <Route path="/onboarding" element={<Private><Onboarding /></Private>} />
              <Route path="/notifications" element={<Private><Notifications /></Private>} />
              <Route path="/redeem" element={<Private><Redeem /></Private>} />
              <Route path="/future-me" element={<Private><FutureMe /></Private>} />
              <Route path="/marketplace" element={<Private><Marketplace /></Private>} />
              <Route path="/marketplace/saved" element={<Private><MarketplaceSaved /></Private>} />
              <Route path="/marketplace/c/:slug" element={<Private><MarketplaceCategory /></Private>} />
              <Route path="/profile/blocked" element={<Private><BlockedUsers /></Private>} />
              <Route path="/goals" element={<Private><Goals /></Private>} />
              <Route path="/goals/:id" element={<Private><GoalDetail /></Private>} />
              <Route path="/achievements" element={<Private><Achievements /></Private>} />
              <Route path="/invite" element={<Private><Invite /></Private>} />
              <Route path="/challenges" element={<Private><ChallengeBoard /></Private>} />
              <Route path="/admin" element={<Private><Admin /></Private>} />
              <Route path="/groups/join/:code" element={<Private><GroupJoin /></Private>} />

              <Route element={<RoleGate><AppLayout /></RoleGate>}>
                <Route path="/app" element={<Dashboard />} />
                <Route path="/feed" element={<Feed />} />
                <Route path="/feed/by-user/:author_id" element={<FeedByUser />} />
                <Route path="/feed/post/:post_id" element={<PostDetail />} />
                <Route path="/dm" element={<DMList />} />
                <Route path="/dm/:user_id" element={<DMChat />} />
                <Route path="/friends" element={<Friends />} />
                <Route path="/coach" element={<Coach />} />
                <Route path="/groups" element={<Groups />} />
                <Route path="/groups/new" element={<GroupCreate />} />
                <Route path="/groups/:id" element={<GroupDetail />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/users/:id" element={<UserProfile />} />
              </Route>
              <Route path="*" element={<JoinHandle />} />
            </Routes>
          </Suspense>
          </NavBadgesProvider>
        </BrowserRouter>
      </AuthProvider>
    </LangProvider>
    </ThemeProvider>
  );
}

export default App;
